import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import "./app-layout.scss";

const menuGroups = [
  { title: "Dashboard", items: [{ label: "Dashboard", icon: "⌂", path: "/vendor", end: true }] },
  { title: "Operations", items: [
    { label: "Pickup Queue", icon: "✓", path: "/vendor/pickup-queue" },
    { label: "Orders", icon: "▦", path: "/vendor/orders" },
  ] },
  { title: "Setup", items: [{ label: "Outlet / Products", icon: "◫", path: "/vendor/outlet-products" }] },
  { title: "Reports", items: [{ label: "Reports", icon: "▥", path: "/vendor/reports" }] },
  { title: "Account", items: [{ label: "Settings", icon: "⚙", path: "/vendor/settings" }] },
];


const outletOptions = [
  { id: 1, name: "Kuching Central", status: "Accepting orders" },
  { id: 2, name: "Vivacity Megamall", status: "Accepting orders" },
  { id: 3, name: "Samarahan Express", status: "Accepting orders" },
];

const pageTitles = {
  "/vendor": "Dashboard",
  "/vendor/pickup-queue": "Pickup Queue",
  "/vendor/orders": "Orders",
  "/vendor/outlet-products": "Outlet / Product Setting",
  "/vendor/reports": "Reports",
  "/vendor/settings": "Settings",
  "/vendor/profile": "Edit Profile",
};

const resolvePageTitle = (pathname) => {
  if (pathname.startsWith("/vendor/orders/")) return "Order Detail";
  if (pathname.includes("/products/create")) return "Add Product";
  if (pathname.includes("/products/") && pathname.includes("/edit")) return "Edit Product";
  return pageTitles[pathname] || "Vendor Portal";
};

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [outletOpen, setOutletOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(outletOptions[0]);

  const handleLogout = () => {
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className="app-layout">
      <aside className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""}`}>
        <div className="app-layout__brand">
          <div className="app-layout__brand-mark">SB</div>
          <div>
            <div className="app-layout__brand-name">SecondBite</div>
            <div className="app-layout__brand-subtitle">Vendor Portal</div>
          </div>
        </div>

        <div className="app-layout__outlet-switcher">
          <div className="app-layout__outlet-label">Outlet</div>
          <button
            className={`app-layout__outlet-button ${outletOpen ? "app-layout__outlet-button--open" : ""}`}
            type="button"
            onClick={() => setOutletOpen((open) => !open)}
          >
            <span className="app-layout__outlet-name">{selectedOutlet.name}</span>
            <span className="app-layout__outlet-chevron">⌄</span>
          </button>

          {outletOpen && (
            <div className="app-layout__outlet-menu" role="menu">
              {outletOptions.map((outlet) => {
                const isSelected = outlet.id === selectedOutlet.id;

                return (
                  <button
                    key={outlet.id}
                    className={`app-layout__outlet-option ${isSelected ? "app-layout__outlet-option--active" : ""}`}
                    type="button"
                    onClick={() => {
                      setSelectedOutlet(outlet);
                      setOutletOpen(false);
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="app-layout__outlet-option-copy">
                      <span className="app-layout__outlet-option-name">{outlet.name}</span>
                      <span className="app-layout__outlet-option-status">{outlet.status}</span>
                    </span>
                    {isSelected && <span className="app-layout__outlet-option-check">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <nav className="app-layout__menu">
          {menuGroups.map((group) => (
            <div className="app-layout__menu-group" key={group.title}>
              <div className="app-layout__menu-title">{group.title}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="app-layout__menu-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="app-layout__mobile-logout">
          <button className="app-layout__logout" type="button" onClick={handleLogout}>
            <span>↩</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && <button className="app-layout__overlay" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />}

      <main className="app-layout__main">
        <header className="app-layout__topbar">
            <button className="app-layout__menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}>☰</button>
            <div>
              <p className="app-layout__eyebrow">MVP B2B Platform</p>
              <h1 className="app-layout__title">{resolvePageTitle(location.pathname)}</h1>
            </div>

            <div className="app-layout__profile-wrap">
            <button className={`app-layout__profile ${profileOpen ? "app-layout__profile--open" : ""}`} type="button" onClick={() => setProfileOpen((open) => !open)}>
              <span className="app-layout__avatar">V</span>
              <span>Vendor</span>
              <span className="app-layout__profile-chevron">⌄</span>
            </button>
            {profileOpen && (
              <>
                <button className="app-layout__profile-backdrop" type="button" aria-label="Close profile menu" onClick={() => setProfileOpen(false)} />
                <div className="app-layout__profile-menu" role="menu">
                  <button className="app-layout__profile-menu-item" type="button" onClick={() => { setProfileOpen(false); navigate("/vendor/profile"); }}>
                    <span className="app-layout__profile-menu-icon">♙</span><span>Edit Profile</span>
                  </button>
                  <button className="app-layout__profile-menu-item app-layout__profile-menu-item--danger" type="button" onClick={handleLogout}>
                    <span className="app-layout__profile-menu-icon">↩</span><span>Logout</span>
                  </button>
                </div>
              </>
            )}
            </div>
        </header>
        <section className="app-layout__content"><Outlet /></section>
      </main>
    </div>
  );
};

export default AppLayout;
